import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { COMPANY_PROFILES } from "../../content/companyProfiles";
import ReviewBadges from "../marketing/ReviewBadges";
import { productUrl } from "../../lib/appUrls";
import styles from "./PublicFooter.module.css";

type FooterLink = { label: string; to: string } | { label: string; href: string };

const FOOTER_COLUMNS: { heading: string; links: FooterLink[] }[] = [
  {
    heading: "Industries",
    links: [
      { label: "Overview", to: "/industries" },
      { label: "Pipe extrusion", to: "/industries/pipe-extrusion-scheduling" },
      { label: "Profile extrusion", to: "/industries/profile-extrusion-scheduling" },
      { label: "Tube & conduit", to: "/industries/tube-conduit-extrusion-scheduling" },
      { label: "Blow molding", to: "/industries/blow-molding-scheduling" },
      { label: "Thermoforming", to: "/industries/thermoforming-scheduling" },
    ],
  },
  {
    heading: "Product",
    links: [
      { label: "Production scheduling software", to: "/production-scheduling-software" },
      { label: "Capacity calculator", to: "/tools/extrusion-production-capacity-calculator" },
      { label: "Spreadsheets to scheduling", to: "/guides/spreadsheet-to-finite-capacity-scheduling" },
      { label: "Best scheduling software", to: "/guides/best-production-scheduling-software-small-manufacturers" },
      { label: "Extrusion scheduling example", to: "/guides/extrusion-production-scheduling-case-study" },
      { label: "RunMark vs MRPeasy", to: "/compare/runmark-vs-mrpeasy" },
      { label: "Katana vs a line scheduler", to: "/compare/katana-vs-finite-capacity-scheduling" },
      { label: "Pricing", to: "/pricing" },
      { label: "Blog", to: "/blog" },
      { label: "Changelog", to: "/changelog" },
      { label: "Sign in", href: productUrl("/login") },
    ],
  },
  {
    heading: "Company",
    links: [
      { label: "Contact", to: "/contact" },
    ],
  },
  {
    heading: "Legal",
    links: [
      { label: "Privacy", to: "/privacy" },
      { label: "Terms", to: "/terms" },
      { label: "Refund policy", to: "/refund-policy" },
      { label: "DPA", to: "/dpa" },
    ],
  },
];

// Review badges load a third-party script, so they're opt-in per page --
// currently only Home.
export default function PublicFooter({ showReviewBadges = false }: { showReviewBadges?: boolean }) {
  const { t } = useTranslation();

  return (
    <footer className={styles.footer}>
      <div className={styles.inner}>
        <div className={styles.brandCol}>
          <Link className={styles.brand} to="/">RunMark</Link>
          <p className={styles.tagline}>
            Production scheduling and traceability for extrusion manufacturers.
          </p>
        </div>
        <nav className={styles.columns} aria-label="Footer">
          {FOOTER_COLUMNS.map((column) => (
            <div className={styles.column} key={column.heading}>
              <h2 className={styles.columnHeading}>{column.heading}</h2>
              <ul className={styles.links}>
                {column.links.map((link) => (
                  <li key={link.label}>
                    {"href" in link ? (
                      <a href={link.href} target="_blank" rel="noopener noreferrer">{link.label}</a>
                    ) : (
                      <Link to={link.to}>{link.label}</Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>
      </div>
      <div className={styles.inner}>
        <div className={styles.profiles}>
          <h2 className={styles.columnHeading} id="footer-profiles-heading">{t("common.findUsOn")}</h2>
          <ul className={styles.profileLinks} aria-labelledby="footer-profiles-heading">
            {COMPANY_PROFILES.map((profile) => (
              <li key={profile.url}>
                <a href={profile.url} target="_blank" rel="noopener noreferrer">{profile.name}</a>
              </li>
            ))}
          </ul>
        </div>
        <div className={styles.badges}>
          <a href="https://enlisted.directory/" title="Listed on Enlisted">
            <img src="https://enlisted.directory/badge/dark.svg" alt="Listed on Enlisted" width="190" height="44" />
          </a>
          {showReviewBadges && <ReviewBadges />}
        </div>
        <span className={styles.copy}>© {new Date().getFullYear()} RunMark · runmark.app</span>
      </div>
    </footer>
  );
}
